import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  AvailableReportType,
  BaseConfig,
  ClientFieldReport,
  EnvironmentConfiguration,
  GeneralConfiguration,
  SearchConfiguration,
  UserConfiguration,
} from '@rx-applications/clientsite/shared/config/utils';
import { validateTrue } from '@rx-applications/shared/utils';
import { CONFIG_FEATURE_KEY } from './config.reducer';
import { ConfigState } from './config.models';
import { selectError as selectAuthError, selectIsAuthenticated } from '@rx-applications/shared/auth/data-access';
import { FeatureFlagEnum } from '@rx-applications/shared/clientsite/feature-flags';

const configState = createFeatureSelector<ConfigState>(CONFIG_FEATURE_KEY);

export const selectLocale = createSelector(configState, (state: ConfigState) => state.locale);

export const selectError = createSelector(configState, (state: ConfigState) => state.error);

export const selectBaseConfiguration = createSelector(configState, (state: ConfigState) => state.baseConfig);

export const selectClientWebsiteId = createSelector(
  configState,
  (state: ConfigState) => state.clientConfig.configuration.general?.websiteId,
);

export const selectClientConfiguration = createSelector(configState, (state) => state.clientConfig.configuration);

export const selectedCurrency = createSelector(
  configState,
  (state) => state.clientConfig.configuration.search.selectedCurrency,
);

export const selectIsCurrencyInitialize = createSelector(
  configState,
  (state) => state.clientConfig.configuration.search.isCurrencyInitialize,
);

export const userSearchConfiguration = createSelector(
  selectClientConfiguration,
  (config: UserConfiguration) => config.search,
);

export const selectUserGeneralConfiguration = createSelector(
  selectClientConfiguration,
  (config: UserConfiguration) => config.general,
);

export const selectUserEmailAddress = createSelector(selectUserGeneralConfiguration, ({ emailAddress }) => emailAddress);

export const selectUserFullName = createSelector(selectUserGeneralConfiguration, ({ fullName }) => fullName);

export const selectAnalyticsEnabled = createSelector(
  selectUserGeneralConfiguration,
  ({ enableAnalytics }) => enableAnalytics,
);

export const selectAllowAutoCancel = createSelector(
  selectClientConfiguration,
  (config: UserConfiguration) => config.reservation.allowAutoCancel,
);

export const selectHideInPolicy = createSelector(
  userSearchConfiguration,
  (config: SearchConfiguration) => config.hideInPolicy,
);

export const selectHasPolicyEnabled = createSelector(
  userSearchConfiguration,
  (config: SearchConfiguration) => config.policyEnabled,
);

export const selectHasTrainlineAccount = createSelector(
  selectUserGeneralConfiguration,
  (config: GeneralConfiguration) => config.hasTrainlineAccount,
);

export const selectLoyaltyEnabled = createSelector(
  selectUserGeneralConfiguration,
  (config: GeneralConfiguration) => config.enableLoyaltyPrograms,
);

export const topLocations = createSelector(
  userSearchConfiguration,
  (config: SearchConfiguration) => config.topLocations || [],
);

export const recentlyBookedHotels = createSelector(
  userSearchConfiguration,
  (config: SearchConfiguration) => config.recentlyBooked || [],
);

export const selectAreEmergencyPhonesEnabled = createSelector(
  selectUserGeneralConfiguration,
  (config: GeneralConfiguration) => config.emergencyPhone,
);

export const selectAvailableReports = createSelector(
  selectClientConfiguration,
  (userConfiguration: UserConfiguration) => userConfiguration.reports.availableReports || [],
);

export const selectClientFieldReports = createSelector(
  selectClientConfiguration,
  (userConfiguration: UserConfiguration) => userConfiguration.reports.clientFieldReports || [],
);

export const selectClientReportsConfig = createSelector(selectClientFieldReports, (clientFieldReports: string[]) =>
  clientFieldReports.map((report) => {
    const reportConfig: ClientFieldReport = {
      name: report,
      url: 'clientfieldvalues',
      id: 'clientfieldvalues',
      queryParams: { clientfieldName: report },
    };
    return reportConfig;
  }),
);

export const selectIsCustomReportAvailable = createSelector(
  selectAvailableReports,
  selectClientFieldReports,
  (availableReports, clientFieldReports) =>
    !!clientFieldReports.length &&
    !!availableReports.find((report: AvailableReportType) => report === AvailableReportType.ClientField),
);

export const selectBookingToMyself = createSelector(
  selectUserGeneralConfiguration,
  ({ fullName, emailAddress }: GeneralConfiguration) => {
    const nameFragments = fullName.split(' ');
    return {
      firstName: nameFragments[0],
      lastName: nameFragments[nameFragments.length - 1],
      email: emailAddress,
    };
  },
);

export const selectContactQueryParams = createSelector(
  selectUserGeneralConfiguration,
  ({ fullName, emailAddress, phoneNumber, company }: GeneralConfiguration) => ({
    name: fullName,
    emailAddress,
    phoneNumber,
    company: company?.name,
  }),
);

export const selectDefaultCurrency = createSelector(
  selectUserGeneralConfiguration,
  ({ defaultCurrency }: GeneralConfiguration) => defaultCurrency,
);

export const selectUserRequiresApproval = createSelector(
  selectUserGeneralConfiguration,
  ({ userRequiresApproval }: GeneralConfiguration) => userRequiresApproval,
);

export const selectRequireApprovalForOutOfPolicyOnly = createSelector(
  selectUserGeneralConfiguration,
  ({ userRequiresApproval, requireApprovalForOutOfPolicyOnly }: GeneralConfiguration) =>
    userRequiresApproval && requireApprovalForOutOfPolicyOnly,
);

export const assignedApprovers = createSelector(
  selectUserGeneralConfiguration,
  ({ approvers }: GeneralConfiguration) => approvers,
);

export const hasApprovers = createSelector(
  selectUserGeneralConfiguration,
  ({ approvers }: GeneralConfiguration) => approvers?.length > 0,
);

export const selectHasMultipleApprovers = createSelector(
  selectUserGeneralConfiguration,
  ({ approvers }: GeneralConfiguration) => approvers?.length > 1,
);

export const selectAllowCompanyPaymentPage = createSelector(
  selectUserGeneralConfiguration,
  ({ allowCompanyPaymentPage }: GeneralConfiguration) => allowCompanyPaymentPage,
);

export const selectAllowIssuingPortal = createSelector(
  selectUserGeneralConfiguration,
  ({ allowPayPortal }: GeneralConfiguration) => allowPayPortal,
);

export const selectCompanyPaymentPageData = createSelector(selectClientConfiguration, ({ general, search }) => ({
  companyName: general.company?.name,
  defaultCurrency: general.defaultCurrency,
  selectedCurrency: search.selectedCurrency,
}));

export const selectAnalyticsCurrency = createSelector(
  configState,
  (state) => state.clientConfig.configuration.reports?.currencyCode,
);

export const selectWebsiteGuid = createSelector(
  configState,
  (state) => state.clientConfig.configuration.general?.websiteGuid,
);

export const selectCid = createSelector(configState, (state: ConfigState) => state.cid);

export const selectFeatureEnabled = (featureName: FeatureFlagEnum) =>
  createSelector(
    selectBaseConfiguration,
    (baseConfig: BaseConfig | null) => baseConfig !== null && baseConfig.features[featureName],
  );

export const selectEnvironmentConfiguration = createSelector(configState, (state: ConfigState) => state.envConfig);

export const selectStripePublishableKey = createSelector(
  selectEnvironmentConfiguration,
  (envConfig: EnvironmentConfiguration) => envConfig.stripe?.pkKey || null,
);

export const selectPaymentCallbackUrl = createSelector(
  selectEnvironmentConfiguration,
  (envConfig) => `${envConfig.paymentwebapi}api/v1/Acs/Acs3dSecure`,
);

export const selectHasEnvironmentConfigurationHttpCompleted = createSelector(
  configState,
  (state: ConfigState) => !!(state.envConfig.authserver.length || state.error),
);

export const selectHasBaseConfigurationHttpCompleted = createSelector(
  configState,
  (state: ConfigState) => !!(state.baseConfig || state.error),
);

export const selectWebsiteId = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.websiteId : 0,
);

export const selectCompanyName = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.companyName : '',
);

export const selectCompanyNameWithApostrophe = createSelector(
  selectCompanyName,
  selectLocale,
  (companyName: string, locale: string) => {
    if (companyName.length < 1) {
      return companyName;
    }
    const isEnglish = /^(en)/.test(locale);
    const endingInS = companyName[companyName.length - 1].toLocaleLowerCase() === 's';

    if (isEnglish) {
      return endingInS ? `${companyName}'` : `${companyName}'s`;
    }
    return endingInS ? companyName : `${companyName}s`;
  },
);

export const selectContactNumber = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.loginContactPhone : 0,
);

export const selectIsSelfSignupEnabled = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.selfSignup.enableSelfSignup : false,
);

export const selectDefaultLocale = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.defaultLocale : null,
);

export const selectIsResetPasswordEnabled = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.enableResetPassword : false,
);

export const selectMaxHotelsInSearch = createSelector(configState, (state: ConfigState) =>
  state.baseConfig ? state.baseConfig.maxHotelsInSearch : 310,
);

export const selectGoogleSearchLocation = createSelector(
  selectFeatureEnabled(FeatureFlagEnum.GoogleLocationSearch),
  (state: boolean) => state,
);

export const selectAreAdditionalPropertyTypesEnabled = createSelector(
  selectFeatureEnabled(FeatureFlagEnum.ShowAdditionalPropertyTypes),
  (areAdditionalPropertyTypesEnabled) => areAdditionalPropertyTypesEnabled,
);

export const selectEnvironmentFeatures = (key: string) =>
  createSelector(selectEnvironmentConfiguration, (envConfig: EnvironmentConfiguration) => {
    const feature = envConfig.features && envConfig.features.find((n) => n[key]);
    return (feature && validateTrue(feature[key])) || false;
  });

export const selectIsConfigurationLoaded = createSelector(configState, (state) =>
  ['LOADED', 'ERROR'].includes(state.clientConfig.loadStatus),
);

export const selectIsRoomexPayPortalEnabled = createSelector(
  selectAllowIssuingPortal,
  selectFeatureEnabled(FeatureFlagEnum.RoomexPayPortal),
  (allowIssuingPortal, featureFlag) => allowIssuingPortal && featureFlag,
);

export const selectIsAuthenticatedAndConfigured = createSelector(
  selectIsAuthenticated,
  selectWebsiteId,
  (isAuthenticated: boolean, id: number) => isAuthenticated && id !== 0,
);

export const selectHasUserConfigurationHttpCompleted = createSelector(
  selectAuthError,
  selectWebsiteId,
  (error: string | null, id: number) => !!(id !== 0 || error),
);

export const selectDisplayUsTaxesAndFees = createSelector(
  selectUserGeneralConfiguration,
  ({ displayUsTaxesAndFees }: GeneralConfiguration) => displayUsTaxesAndFees,
);

export const selectHideDueAtCheckIn = createSelector(
  selectUserGeneralConfiguration,
  ({ hideDueAtCheckIn }: GeneralConfiguration) => hideDueAtCheckIn,
);

export const selectTrainlineHyperlink = createSelector(
  selectEnvironmentConfiguration,
  (envConfig: EnvironmentConfiguration) => envConfig.trainlinehyperlink,
);

export const selectApprovalFlowEnabled = createSelector(
  selectUserGeneralConfiguration,
  ({ approvalFlowEnabled }: GeneralConfiguration) => approvalFlowEnabled,
);

export const selectBranding = createSelector(
  configState,
  (state: ConfigState) => state.baseConfig.brandConfig
)
